import { registerMockAPI } from '~/mocks/mockApis';

import { useMutation } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypePutClaimParams, TypePutClaimResponse } from '~/types/receive.types';

/**
 * [입고관리 > 회송/폐기 관리] 클레임 처리 신청 API
 *
 * @note 이미지를 함께 보내므로 멀티파트로 전송한다(배열 필드는 동일 키 반복 append). 에러는 사용처(모달)에서 처리.
 */
const fetchPutReturnDisposalClaim = async (params: TypePutClaimParams): Promise<{ inspectionIds: number[] }> => {
  const { inspectionIds, reason, images } = params;

  const formData = new FormData();
  inspectionIds.forEach((id) => formData.append('inspectionIds', String(id)));
  formData.append('reason', reason);
  images.forEach((file) => formData.append('images', file));

  const { data } = await axiosInstance.put<TypePutClaimResponse>(
    API_ENDPOINT.RECEIVE.PUT_RETURN_DISPOSAL_CLAIM,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );

  return data.data;
};

/**
 * [입고관리 > 회송/폐기 관리] 클레임 처리 신청 Mutation Hook
 */
export const usePutReturnDisposalClaimMutation = () => {
  return useMutation({
    mutationFn: fetchPutReturnDisposalClaim,
    meta: { showGlobalErrorToast: false },
  });
};

/**
 * [입고관리 > 회송/폐기 관리] 클레임 처리 신청 Mock
 */
registerMockAPI<TypePutClaimResponse | TypeErrorResponseData>({
  method: 'put',
  endpoint: API_ENDPOINT.RECEIVE.PUT_RETURN_DISPOSAL_CLAIM,
  label: '회송폐기관리/클레임 신청',
  scenarios: [
    {
      name: '신청 성공',
      response: { data: { inspectionIds: [1, 2] } },
    },
    {
      name: '서버 에러 (500)',
      response: { error: { code: 500, message: '신청에 실패했습니다.\n다시 시도해주세요.' } },
      status: 500,
    },
  ],
});
